import FilterIcon from "@assets/icons/new/filter_24px.svg?react";
import FilterWithBadgeIcon from "@assets/icons/new/filter_with_badge_24px.svg?react";
import { ReactSetStateFunction } from "@custom-types/types";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Stack, SvgIcon, Typography } from "@mui/material";
import { EDecimalToHex } from "@styles/common-colors";
import { blue, neutral } from "@flat-ui";
import { addTransparency } from "@utils/ui-utils";
import { useMemo } from "react";

interface Props {
  /** Whether to show filter chips or not */
  shouldShowFilterChips?: boolean;

  /** Setter for shouldShowFilterChips */
  setShouldShowFilterChips?: ReactSetStateFunction<boolean>;
}

/** The filter title and icon shown at the beginning of filter toolbar */
export function FilterToolbarTitle({
  shouldShowFilterChips = true,
  setShouldShowFilterChips,
}: Props): JSX.Element {
  const { isScreenMdOrSmaller } = useMediaQueryList();
  const { isAnyFilterApplied } = useFilterQueryParams();

  /** Whether filter chips are visible on small screen or not */
  const areFilterChipsVisibleInSmallScreen =
    isScreenMdOrSmaller && shouldShowFilterChips;

  const filterIcon = useMemo(() => {
    if (isScreenMdOrSmaller && isAnyFilterApplied && !shouldShowFilterChips) {
      return (
        <SvgIcon
          inheritViewBox
          component={FilterWithBadgeIcon}
          sx={{ width: "18px", height: "18px" }}
        />
      );
    }
    return (
      <SvgIcon
        inheritViewBox
        component={FilterIcon}
        sx={{ width: "18px", height: "18px" }}
      />
    );
  }, [isAnyFilterApplied, isScreenMdOrSmaller, shouldShowFilterChips]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={() => setShouldShowFilterChips?.(!shouldShowFilterChips)}
      sx={{
        cursor: isScreenMdOrSmaller ? "pointer" : "default",
        px: "10px",
        py: "6px",
        "&:hover": {
          backgroundColor: isScreenMdOrSmaller
            ? addTransparency({
                color: neutral[500],
                alpha: EDecimalToHex.twentySix,
              })
            : "",
          color: isScreenMdOrSmaller ? blue[500] : "",
        },
        backgroundColor: areFilterChipsVisibleInSmallScreen
          ? addTransparency({
              color: neutral[500],
              alpha: EDecimalToHex.thirtyEight,
            })
          : "",

        color: areFilterChipsVisibleInSmallScreen ? blue[500] : neutral[1000],
      }}
    >
      {filterIcon}
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          ml: "8px",
        }}
      >
        Filters
      </Typography>
    </Stack>
  );
}
